import { nextTick, type Ref, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";

export function useInsightFeedAccessibility(root: Ref<HTMLElement | null>, ids: () => string[], exhausted: () => boolean, focusTargetId?: () => string | undefined) {
    const { t } = useI18n();
    const announcement = ref("");
    const status = shallowRef<HTMLElement | null>(null);
    let removedFocus: HTMLElement | null = null;
    let preferredId: string | undefined;

    function restoreFocus() {
        if (!removedFocus) {
            return;
        }
        const active = document.activeElement;
        if (active !== removedFocus && active !== document.body) {
            removedFocus = null;
            return;
        }
        const cards = Array.from(root.value?.querySelectorAll<HTMLElement>("[data-insight-id]") ?? []);
        const target = cards.find((card) => card.dataset.insightId === (focusTargetId?.() ?? preferredId))?.querySelector<HTMLElement>("[data-insight-heading]");
        if (target || !ids().length) {
            (target ?? status.value)?.focus({ preventScroll: true });
            removedFocus = null;
        }
    }

    watch(
        ids,
        (next, previous) => {
            const removed = previous.filter((id) => !next.includes(id));
            if (!removed.length) {
                return;
            }
            const message = exhausted() ? t("myWorkInsightFeed.exhausted") : t("myWorkInsightFeed.updated");
            announcement.value = "";
            void nextTick(() => {
                announcement.value = message;
            });
            const active = document.activeElement;
            if (!(active instanceof HTMLElement) || !root.value?.contains(active)) {
                return;
            }
            const card = active.closest<HTMLElement>("[data-insight-id]");
            if (!card || !removed.includes(card.dataset.insightId ?? "")) {
                return;
            }
            removedFocus = active;
            preferredId = next.find((id) => !previous.includes(id)) ?? next[Math.min(previous.indexOf(card.dataset.insightId!), next.length - 1)];
            void nextTick(restoreFocus);
        },
        { flush: "pre" },
    );

    function focusAfterRetirement() {
        if (!root.value?.contains(document.activeElement)) {
            return;
        }
        const main = root.value.closest("main") ?? document.querySelector("main");
        const target = Array.from(main?.querySelectorAll<HTMLElement>("textarea, input, h1, h2") ?? []).find((element) => !root.value?.contains(element));
        if (target) {
            if (target.matches("h1, h2")) {
                target.tabIndex = -1;
            }
            target.focus();
        }
    }

    return { announcement, status, restoreFocus, focusAfterRetirement };
}
